// Timezone storage utilities for coral-ko
// Manages user's timezone preference in localStorage

/**
 * Get the browser's detected timezone
 * @returns {string} IANA timezone identifier (e.g., "Asia/Bangkok")
 */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {
    console.error('Failed to detect browser timezone:', e)
    return 'Asia/Bangkok' // Fallback to Bangkok
  }
}

/**
 * Get the user's selected timezone from localStorage, or browser timezone as fallback
 * @returns {string} IANA timezone identifier
 */
export const getUserTimezone = () => {
  const savedTimezone = localStorage.getItem('userTimezone')
  if (savedTimezone) {
    return savedTimezone
  }
  return getBrowserTimezone()
}

/**
 * Save the user's timezone preference to localStorage
 * @param {string} timezone - IANA timezone identifier (e.g., "Asia/Tokyo")
 */
export const saveUserTimezone = (timezone) => {
  if (!timezone) {
    console.error('Cannot save empty timezone')
    return
  }
  localStorage.setItem('userTimezone', timezone)
}

/**
 * Clear the user's timezone preference from localStorage
 * This will cause the system to use the browser's timezone
 */
export const clearUserTimezone = () => {
  localStorage.removeItem('userTimezone')
}

/**
 * Get UTC offset for a timezone
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Formatted UTC offset (e.g., "UTC+7", "UTC+5:30")
 */
export const getUTCOffset = (timezone) => {
  try {
    const now = new Date()
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
    const offsetMinutes = (tzDate - utcDate) / (1000 * 60)
    const offsetHours = offsetMinutes / 60

    const sign = offsetHours >= 0 ? '+' : '-'
    const absHours = Math.abs(Math.floor(offsetHours))
    const minutes = Math.abs(offsetMinutes % 60)

    if (minutes === 0) {
      return `UTC${sign}${absHours}`
    } else {
      return `UTC${sign}${absHours}:${minutes.toString().padStart(2, '0')}`
    }
  } catch (error) {
    return 'UTC'
  }
}

/**
 * Extract city name from timezone identifier
 * @param {string} timezone - IANA timezone identifier (e.g., "Asia/Tokyo")
 * @returns {string} City name (e.g., "Tokyo")
 */
export const getCityName = (timezone) => {
  const parts = timezone.split('/')
  return parts[parts.length - 1].replace(/_/g, ' ')
}

/**
 * Get all supported timezones from browser with UTC offsets
 * @returns {Array<{value: string, label: string}>} Array of timezone options
 */
export const getAllTimezones = () => {
  try {
    // Get all supported timezones
    const allTimezones = Intl.supportedValuesOf('timeZone')

    // Map to format with city names and UTC offset
    return (
      allTimezones
        .map((tz) => {
          const cityName = getCityName(tz)
          const offset = getUTCOffset(tz)

          return {
            value: tz,
            label: `${cityName} (${offset})`,
          }
        })
        // Sort alphabetically by city name
        .sort((a, b) => a.label.localeCompare(b.label))
    )
  } catch (error) {
    // Fallback if Intl.supportedValuesOf is not available
    console.warn(
      'Intl.supportedValuesOf not supported, using fallback timezones',
    )
    return [
      { value: 'Asia/Bangkok', label: 'Bangkok (UTC+7)' },
      { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
      { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)' },
      { value: 'Asia/Singapore', label: 'Singapore (UTC+8)' },
      { value: 'Asia/Seoul', label: 'Seoul (UTC+9)' },
      { value: 'Asia/Taipei', label: 'Taipei (UTC+8)' },
      { value: 'Asia/Jakarta', label: 'Jakarta (UTC+7)' },
      { value: 'Asia/Manila', label: 'Manila (UTC+8)' },
    ]
  }
}

/**
 * List of common timezones for kiosk application
 * @deprecated Use getAllTimezones() instead for full browser-supported list
 */
export const COMMON_TIMEZONES = getAllTimezones()
